/**
 * BenchmarkRunDetail - Detailed view of a single benchmark run
 *
 * Shows run summary stats (AVG/MEDIAN/MIN/MAX PoP, AVG Peak Reduction)
 * and a per-scenario results table with PoP color coding.
 */

import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    IconButton,
    CircularProgress,
    TableRow,
    TableCell,
    Chip,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import {
    vortxColors,
    vortxTypography,
    ProjectTableContainer,
    ProjectTableHead,
    ProjectTableHeaderCell,
    ProjectTableBody,
} from '@vortx/shared-ui';
import { useBenchmarkRunDetail } from '../../../hooks';
import BenchmarkVisualization from './BenchmarkVisualization';
import TrainingReport from './TrainingReport';

const STATUS_COLORS = {
    submitted: '#9b59b6',
    queued: '#9b59b6',
    running: '#2196F3',
    completed: '#5fd4c4',
    failed: vortxColors.error,
};

function getPopColor(pop) {
    if (pop == null) return '#666';
    if (pop >= 85) return '#5fd4c4';
    if (pop >= 70) return '#9b59b6';
    return vortxColors.error;
}

function StatCard({ label, value, unit = '%', color }) {
    return (
        <Box sx={{
            flex: '1 1 140px',
            bgcolor: 'rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(155, 89, 182, 0.2)',
            borderRadius: 1,
            p: 2,
            textAlign: 'center',
        }}>
            <Typography sx={{
                fontFamily: vortxTypography.mono,
                fontSize: '0.6rem',
                fontWeight: 600,
                color: '#888',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                mb: 0.5,
            }}>
                {label}
            </Typography>
            <Typography sx={{
                fontFamily: vortxTypography.mono,
                fontSize: '1.2rem',
                fontWeight: 700,
                color: color || '#5fd4c4',
            }}>
                {value != null ? `${value.toFixed(1)}${unit}` : '--'}
            </Typography>
        </Box>
    );
}

export default function BenchmarkRunDetail({ runId, onBack }) {
    const { run, scenarios, loading } = useBenchmarkRunDetail(runId);
    const [showResults, setShowResults] = useState(false);
    const prevStatusRef = useRef(null);

    // Crossfade: when run completes, delay then show results
    useEffect(() => {
        if (!run) return;
        const wasRunning = prevStatusRef.current && prevStatusRef.current !== 'completed';
        prevStatusRef.current = run.status;

        if (run.status === 'completed') {
            if (wasRunning) {
                // Transition delay for the "settling" effect
                const timer = setTimeout(() => setShowResults(true), 2000);
                return () => clearTimeout(timer);
            }
            // Already completed on load — show results immediately
            setShowResults(true);
        }
    }, [run?.status]);

    const isVisualizing = !showResults && run && run.status !== 'failed';

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress size={28} sx={{ color: '#9b59b6' }} />
            </Box>
        );
    }

    if (!run) {
        return (
            <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <IconButton onClick={onBack} size="small" sx={{ color: '#9b59b6' }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.85rem',
                        color: vortxColors.error,
                    }}>
                        Run not found
                    </Typography>
                </Box>
            </Box>
        );
    }

    const statusColor = STATUS_COLORS[run.status] || '#888';
    const summary = run.summary || {};

    // Sort scenarios by cost PoP descending
    const sortedScenarios = [...scenarios].sort((a, b) => {
        const aPop = a.cost_pop_pct ?? -1;
        const bPop = b.cost_pop_pct ?? -1;
        return bPop - aPop;
    });

    return (
        <Box sx={{ p: 3 }}>
            {/* Header: Back + Label + Status */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <IconButton onClick={onBack} size="small" sx={{ color: '#9b59b6' }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography sx={{
                    fontFamily: vortxTypography.heading,
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    color: '#9b59b6',
                    flex: 1,
                }}>
                    {run.label || run.run_id}
                </Typography>
                <Chip
                    label={run.status.toUpperCase()}
                    size="small"
                    sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        letterSpacing: '0.08em',
                        color: statusColor,
                        borderColor: statusColor,
                        bgcolor: 'transparent',
                    }}
                    variant="outlined"
                />
            </Box>

            {/* Neural network visualization while running */}
            {isVisualizing && (
                <Box sx={{
                    opacity: showResults ? 0 : 1,
                    transition: 'opacity 0.8s ease',
                    pointerEvents: showResults ? 'none' : 'auto',
                }}>
                    <BenchmarkVisualization run={run} scenarios={scenarios} />
                </Box>
            )}

            {/* Results: stat cards + table (fade in when complete) */}
            {showResults && (
                <Box sx={{
                    animation: 'fadeInUp 0.6s ease-out',
                    '@keyframes fadeInUp': {
                        '0%': { opacity: 0, transform: 'translateY(20px)' },
                        '100%': { opacity: 1, transform: 'translateY(0)' },
                    },
                }}>
                    {/* Summary stats row */}
                    <Box sx={{
                        display: 'flex',
                        gap: 2,
                        mb: 3,
                        flexWrap: 'wrap',
                    }}>
                        <StatCard
                            label="Cost PoP"
                            value={summary.mean_cost_pop_pct}
                            color={getPopColor(summary.mean_cost_pop_pct)}
                        />
                        <StatCard
                            label="Peak PoP"
                            value={summary.mean_peak_pop_pct}
                            color={getPopColor(summary.mean_peak_pop_pct)}
                        />
                        <StatCard
                            label="Completed"
                            value={summary.completed}
                            unit=""
                        />
                        <StatCard
                            label="Failed"
                            value={summary.failed}
                            unit=""
                            color={summary.failed > 0 ? vortxColors.error : '#5fd4c4'}
                        />
                    </Box>

                    {/* Training report (if auto-train ran) */}
                    {run.training_report && (
                        <TrainingReport report={run.training_report} />
                    )}

                    {/* Per-scenario table */}
                    {sortedScenarios.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography sx={{
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.8rem',
                                color: '#888',
                            }}>
                                No scenario results
                            </Typography>
                        </Box>
                    ) : (
                        <ProjectTableContainer>
                            <ProjectTableHead>
                                <ProjectTableHeaderCell>Building</ProjectTableHeaderCell>
                                <ProjectTableHeaderCell>Climate</ProjectTableHeaderCell>
                                <ProjectTableHeaderCell>Tariff</ProjectTableHeaderCell>
                                <ProjectTableHeaderCell align="center">EV</ProjectTableHeaderCell>
                                <ProjectTableHeaderCell align="center">Cold</ProjectTableHeaderCell>
                                <ProjectTableHeaderCell align="right">Siloed Peak</ProjectTableHeaderCell>
                                <ProjectTableHeaderCell align="right">Orch. Peak</ProjectTableHeaderCell>
                                <ProjectTableHeaderCell align="right">Global Peak</ProjectTableHeaderCell>
                                <ProjectTableHeaderCell align="right">PoP</ProjectTableHeaderCell>
                            </ProjectTableHead>
                            <ProjectTableBody>
                                {sortedScenarios.map((scenario) => {
                                    const cfg = scenario.config || {};
                                    const pop = scenario.cost_pop_pct;
                                    const popColor = getPopColor(pop);
                                    const hasEv = cfg.ev_preset && cfg.ev_preset !== 'none';
                                    const hasCold = cfg.cold_preset && cfg.cold_preset !== 'none';

                                    return (
                                        <TableRow key={scenario.scenario_id}>
                                            <TableCell sx={{
                                                fontFamily: vortxTypography.mono,
                                                fontSize: '0.75rem',
                                                color: vortxColors.white,
                                                borderBottom: '1px solid rgba(155, 89, 182, 0.2)',
                                            }}>
                                                {cfg.building_type || '--'}
                                            </TableCell>
                                            <TableCell sx={{
                                                fontFamily: vortxTypography.mono,
                                                fontSize: '0.75rem',
                                                color: '#aaa',
                                                borderBottom: '1px solid rgba(155, 89, 182, 0.2)',
                                            }}>
                                                {cfg.climate_zone || '--'}
                                            </TableCell>
                                            <TableCell sx={{
                                                fontFamily: vortxTypography.mono,
                                                fontSize: '0.75rem',
                                                color: '#aaa',
                                                borderBottom: '1px solid rgba(155, 89, 182, 0.2)',
                                            }}>
                                                {cfg.tariff_variant || '--'}
                                            </TableCell>
                                            <TableCell align="center" sx={{
                                                fontFamily: vortxTypography.mono,
                                                fontSize: '0.75rem',
                                                color: hasEv ? '#5fd4c4' : '#555',
                                                borderBottom: '1px solid rgba(155, 89, 182, 0.2)',
                                            }}>
                                                {hasEv ? cfg.ev_preset : '--'}
                                            </TableCell>
                                            <TableCell align="center" sx={{
                                                fontFamily: vortxTypography.mono,
                                                fontSize: '0.75rem',
                                                color: hasCold ? '#5fd4c4' : '#555',
                                                borderBottom: '1px solid rgba(155, 89, 182, 0.2)',
                                            }}>
                                                {hasCold ? cfg.cold_preset : '--'}
                                            </TableCell>
                                            <TableCell align="right" sx={{
                                                fontFamily: vortxTypography.mono,
                                                fontSize: '0.75rem',
                                                color: '#aaa',
                                                borderBottom: '1px solid rgba(155, 89, 182, 0.2)',
                                            }}>
                                                {scenario.siloed_peak_kw != null ? `${scenario.siloed_peak_kw.toFixed(0)} kW` : '--'}
                                            </TableCell>
                                            <TableCell align="right" sx={{
                                                fontFamily: vortxTypography.mono,
                                                fontSize: '0.75rem',
                                                color: '#aaa',
                                                borderBottom: '1px solid rgba(155, 89, 182, 0.2)',
                                            }}>
                                                {scenario.orchestrated_peak_kw != null ? `${scenario.orchestrated_peak_kw.toFixed(0)} kW` : '--'}
                                            </TableCell>
                                            <TableCell align="right" sx={{
                                                fontFamily: vortxTypography.mono,
                                                fontSize: '0.75rem',
                                                color: '#aaa',
                                                borderBottom: '1px solid rgba(155, 89, 182, 0.2)',
                                            }}>
                                                {scenario.global_optimum_peak_kw != null ? `${scenario.global_optimum_peak_kw.toFixed(0)} kW` : '--'}
                                            </TableCell>
                                            <TableCell align="right" sx={{
                                                fontFamily: vortxTypography.mono,
                                                fontSize: '0.85rem',
                                                fontWeight: 700,
                                                color: popColor,
                                                borderBottom: '1px solid rgba(155, 89, 182, 0.2)',
                                            }}>
                                                {pop != null ? `${pop.toFixed(1)}%` : '--'}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </ProjectTableBody>
                        </ProjectTableContainer>
                    )}
                </Box>
            )}
        </Box>
    );
}
